import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessage } from 'react-intl';
import { Outlet, useNavigate, useSearchParams } from 'react-router';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_REWARDS_ESSENCE_TIER_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { craftableRarities } from '@sorare/core/src/lib/cards';

import {
  ALL_COMPETITIONS,
  COMPETITION_SEARCH_PARAM,
  PoolCompetitionFilter,
} from '#src/components/rewards/PoolCompetitionFilter/index.tsx';
import { PoolRarityToggle } from '#src/components/rewards/PoolRarityToggle/index.tsx';
import { PoolTierFilter } from '#src/components/rewards/PoolTierFilter/index.tsx';
import { useCraftingPoolTiers } from '#src/hooks/useCraftingPoolPath.ts';

import type {
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const REWARDS_ESSENCE_LAYOUT_QUERY = gql`
  query RewardsEssenceLayoutQuery(
    $sport: Sport!
    $rarity: Rarity!
    $competitionSlugs: [String!]!
    $isFootball: Boolean!
  ) {
    cardShardsPoolCompetitions(sport: $sport, rarity: $rarity) {
      slug
      ...PoolCompetitionFilter_competition
    }
    football @include(if: $isFootball) {
      competitions(slugs: $competitionSlugs) {
        slug
        ...PoolCompetitionFilter_competition
      }
    }
  }
  ${PoolCompetitionFilter.fragments.competition}
` as TypedDocumentNode<
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables
>;

export type CraftingPoolOutletContext = {
  competitionNotCraftableYet: boolean;
};

type Props = {
  tier?: string;
  rarity?: string;
};

export const RewardsEssenceLayout = ({ tier, rarity }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tiers = useCraftingPoolTiers();
  const rarities = craftableRarities(sport);

  const selectedRarity = rarities.find(r => r === rarity) || rarities[0];
  const selectedCompetitionSlug =
    searchParams.get(COMPETITION_SEARCH_PARAM) || ALL_COMPETITIONS;

  const { data } = useQuery(REWARDS_ESSENCE_LAYOUT_QUERY, {
    variables: {
      sport,
      rarity: selectedRarity,
      competitionSlugs:
        selectedCompetitionSlug === ALL_COMPETITIONS
          ? []
          : [selectedCompetitionSlug],
      isFootball: sport === Sport.FOOTBALL,
    },
  });
  const availableCompetitions = data?.cardShardsPoolCompetitions || [];

  const upcomingCompetition = data?.football?.competitions.find(
    competition =>
      !availableCompetitions.some(({ slug }) => slug === competition.slug)
  );

  const poolPath = (params: { tier: string; rarity: Rarity }) => ({
    pathname: generateSportContextPath(ANY_SPORT_REWARDS_ESSENCE_TIER_RARITY, {
      params,
    }),
    search: searchParams.toString(),
  });

  return (
    <div className="py-2">
      <PageHeader
        text={defineMessage({
          defaultMessage: 'Crafting pool',
        })}
      />
      <FullWidthContainer>
        <Vertical gap={2}>
          <Horizontal>
            <PoolTierFilter
              tiers={tiers}
              selectedTierSlug={tier || ''}
              onChange={newTier =>
                navigate(poolPath({ tier: newTier, rarity: selectedRarity }), {
                  replace: true,
                })
              }
            />
            <PoolCompetitionFilter
              competitions={availableCompetitions}
              upcomingCompetition={upcomingCompetition}
              selectedCompetitionSlug={selectedCompetitionSlug}
              onChange={competitionSlug =>
                setSearchParams(
                  params => {
                    if (competitionSlug === ALL_COMPETITIONS) {
                      params.delete(COMPETITION_SEARCH_PARAM);
                    } else {
                      params.set(COMPETITION_SEARCH_PARAM, competitionSlug);
                    }
                    return params;
                  },
                  { replace: true }
                )
              }
            />
          </Horizontal>
          {!!upcomingCompetition && (
            <Alert
              icon={
                <FontAwesomeIcon icon={iconClock} color="var(--c-yellow-400)" />
              }
              title={
                <FormattedMessage
                  defaultMessage="{competition} is not craftable yet"
                  values={{ competition: upcomingCompetition.displayName }}
                />
              }
              message={
                <BodyM as="p" color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Its Cards join the crafting pool later this season. Pick another competition to see what you can craft today." />
                </BodyM>
              }
            />
          )}
          {!!tier && (
            <PoolRarityToggle
              rarities={[...rarities]}
              selectedRarity={selectedRarity}
              onChange={newRarity =>
                navigate(poolPath({ tier, rarity: newRarity }), {
                  replace: true,
                })
              }
            />
          )}
          <Outlet
            context={
              {
                competitionNotCraftableYet: !!upcomingCompetition,
              } satisfies CraftingPoolOutletContext
            }
          />
        </Vertical>
      </FullWidthContainer>
    </div>
  );
};
